import { useEffect, useState, useRef, RefObject, useMemo } from 'react';
import {
  generatePath,
  NavLink,
  Outlet,
  useParams,
  useOutletContext,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import queryString from 'query-string';
import groupBy from 'lodash.groupby';
import { useForm } from 'react-hook-form';
import {
  InputText,
  IconSearch,
  IconDownload,
  IconTrash,
  IconArrowRight,
  Popup,
  IconExpand,
  IconUp,
  IconDown,
  IconAgreements,
  IconAttention,
  IconLoader,
  PermissionEnum,
  IconSelected,
  IconBlock,
  PermissionDenied,
  ClickOutsideDetector,
  IconLeft,
  IconRight,
  usePrevious,
  FilterTag,
  responseErrorsResolver,
  ResponseError,
  useGetList,
  useQueryState,
  SchemaFilterType,
  IconArrowLeft,
  Table,
  formatDate,
  IconClock,
  IconChat,
  IconEdit,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onSelectDocument,
  onSelectDocumentById,
  setBlockView,
  onSelectAllGroup,
  clearSelectedState,
  deleteIsSuccess,
  onSelectAllGroupById,
  onDocumentsMoveById,
  onDocumentRaname,
  setNeedToReloadCategoryId,
  setCursor,
  clearCursorByCategoryId,
  clearAllCursors,
  setCursorByCategoryId,
} from '../store/slices/documents/documents';
import {
  CustomerAgreement,
  CustomerDocument,
  CustomerDocumentStatus,
  CustomerType,
  CustomerAgreementDictionary,
  CustomerStatus,
} from '../types';
import {
  useLazyGetCustomerDocumentsQuery,
  useLazyGetExpiredDocumentsQuery,
  useLazyGetCustomerDocumentFileQuery,
  useChangeCustomerDocumentsStatusMutation,
  useBatchDeleteCustomerDocumentsMutation,
  useChangeCustomerDocumentsCategoryMutation,
  useLazyGetCustomerAgreementsQuery,
  useLazyGetCustomerUnsignedAgreementsQuery,
  useLazyGetCustomerOrganizationAllowedDocumentsCategoriesQuery,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
  useRenameCustomerDocumentMutation,
  useDeleteCustomerDocumentMutation,
} from '../store/services/customers';
import CorporateDocuments from '../../../components/partial/documents/corporate-documents';
import AgreementsHeader from './agreements-header';
import AgreementContent from './agreement-content';
import ROUTES from '../../../routes';
import { columns, columnsLabels, schema } from './variables/expired';

import cn from '../customers.module.css';

let timeout: NodeJS.Timeout;

const formatExpiredDocumentsList = (
  list: CustomerDocument[],
  setSubMenuVisible: (data: boolean) => void,
  setSelectedSubMenuDocumentId: (data: string) => void,
  setRenamePopupVisible: (data: boolean) => void,
  setSelectedDocumentToRename: (data: CustomerDocument) => void,
  getFile: (data: CustomerDocument) => void,
  setDeletePopupVisible: (data: boolean) => void,
  setSelectedDeleteDocument: (data: CustomerDocument) => void,
  customerType: CustomerType,
  subMenuVisible?: boolean,
  selectedSubMenuDocumentId?: string,
  documentRenameAvailable?: boolean,
  activePermissions?: PermissionEnum[],
  search?: string,
): CustomerDocument[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      createdAt: <div>{formatDate(result.createdAt)}</div>,
      expirationDate: (
        <div className={cn.expiredDocumentRow}>
          {formatDate(result.expirationDate)}{' '}
          <IconClock className={cn.expiredIcon} />
        </div>
      ),
      status: (
        <div className={cn.expiredStatus}>
          <div className={cn.expiredStatusLeft}>
            <span>Expired</span>
            <span>
              <IconAttention className={cn.expiredStatusIcon} />
            </span>
          </div>

          <div className={cn.documentRowRightContentRight}>
            <div className={cn.documentsRowComments}>
              {item?.countComments && item?.countComments.count > 0 && (
                <>
                  <IconChat
                    className={cn.documentsRowCommentIcon}
                    width={14}
                    height={14}
                  />
                  {item?.countComments.count}
                </>
              )}
            </div>
            <div
              className={`${cn.expiredDocumentMenuWrapper} ${
                subMenuVisible && selectedSubMenuDocumentId === item.id
                  ? cn.expiredDocumentMenuWrapperActive
                  : ''
              }`}
            >
              <button
                type="button"
                aria-label="Show menu"
                className={`${cn.expiredDocumentsRowMenuButton} ${
                  subMenuVisible && selectedSubMenuDocumentId === item.id
                    ? cn.expiredDocumentsRowMenuButtonActive
                    : ''
                }`}
                onClick={() => {
                  setSubMenuVisible(!subMenuVisible);

                  if (subMenuVisible) {
                    setSelectedSubMenuDocumentId('');
                  } else {
                    setSelectedSubMenuDocumentId(item.id);
                  }
                }}
              >
                <span />
              </button>
              {subMenuVisible && selectedSubMenuDocumentId === item.id && (
                <ul className={cn.expiredDocumentsRowMenu}>
                  {documentRenameAvailable && (
                    <li>
                      <button
                        type="button"
                        className={cn.documentsMenuButton}
                        onClick={() => {
                          setRenamePopupVisible(true);
                          setSelectedDocumentToRename(item);
                          setSubMenuVisible(false);
                          setSelectedSubMenuDocumentId('');
                        }}
                      >
                        <IconEdit className={cn.documentsMenuButtonIcon} />
                        Rename
                      </button>
                    </li>
                  )}
                  <li>
                    <NavLink
                      to={`${item.url}${search || ''}#comments`}
                      className={`${cn.documentsMenuButton} ${cn.documentMenuButtonLink}`}
                      onClick={() => {
                        setSubMenuVisible(false);
                        setSelectedSubMenuDocumentId('');
                      }}
                    >
                      <IconChat
                        className={`${cn.documentsMenuButtonIcon} ${cn.documentCommentIcon}`}
                      />
                      Comments
                    </NavLink>
                  </li>
                  <li>
                    <button
                      type="button"
                      className={cn.documentsMenuButton}
                      onClick={() => getFile(item)}
                    >
                      <IconDownload className={cn.documentsMenuButtonIcon} />
                      Download
                    </button>
                  </li>
                  {((customerType === CustomerType.INDIVIDUAL &&
                    activePermissions?.includes(
                      PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                    )) ||
                    (customerType === CustomerType.CORPORATE &&
                      activePermissions?.includes(
                        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                      ))) && (
                    <li>
                      <button
                        type="button"
                        className={cn.documentsMenuButton}
                        onClick={() => {
                          setDeletePopupVisible(true);
                          setSubMenuVisible(false);
                          setSelectedDeleteDocument(item);
                        }}
                      >
                        <IconTrash
                          className={`${cn.documentsMenuButtonIcon} ${cn.documentDeleteIcon}`}
                        />
                        Delete
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CustomerDocuments = ({
  customerType,
}: {
  customerType: CustomerType;
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [agreementsVisible, setAgreementsVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    CustomerDocumentStatus | undefined
  >();
  const [otherActionsMenuVisible, setOtherActionsMenuVisible] = useState(false);
  const [agreementsList, setAgreementsList] = useState<
    CustomerAgreementDictionary<CustomerAgreement>
  >({});
  const [activeLabel, setActiveLabel] = useState(false);
  const [withScrollButton, setWithScrollButton] = useState(false);
  const [currentScrollValue, setCurrentValueScroll] = useState(0);
  const [contentScrollWidth, setContentScrollWidth] = useState(0);
  const [withLeftButton, setWithLeftButton] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    category: string;
  }>();
  const [renamePopupVisible, setRenamePopupVisible] = useState(false);
  const [selectedDocumentToRename, setSelectedDocumentToRename] = useState<
    CustomerDocument | undefined
  >();
  const [documentName, setDocumentName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sort, setSort] = useState('asc');
  const [sortProp, setSortProp] = useState('createdAt');
  const [nameError, setNameError] = useState('');
  const [
    selectedDocumentsCountByCategory,
    setSelectedDocumentsCountByCategory,
  ] = useState(0);
  const [expiredDocumentsVisible, setExpiredDocumentsVisible] = useState(false);
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const [selectedSubMenuDocumentId, setSelectedSubMenuDocumentId] =
    useState('');
  const [
    deleteExpiredDocumentPopupVisible,
    setDeleteExpiredDocumentPopupVisible,
  ] = useState(false);
  const [selectedDeleteDocument, setSelectedDeleteDocument] = useState<
    CustomerDocument | undefined
  >(undefined);

  const prevScrollValue = usePrevious(currentScrollValue);

  const scrollWrapper = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const { wrapperRef } = useOutletContext<{
    wrapperRef: RefObject<HTMLDivElement | null>;
  }>();

  const {
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
    customerDocuments: {
      agreements,
      usignedAgreements,
      stats,
      allowedCategories,
      allDocuments,
      documentsByCategoryId,
      allDocumentsSelected,
      allDocumentsTotal,
      documentsByCategoryTotal,
      selectedDocumentsByGroupId,
      needToReloadCategoryId,
      cursor,
      cursorByCategoryId,
      expiredDocumentsPagination,
      expiredDocuments,
    },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { customerId } = useParams();

  const [getCustomerDocuments, { isLoading, isFetching: documentsIsFetching }] =
    useLazyGetCustomerDocumentsQuery();
  const [
    getExpiredDocuments,
    {
      isLoading: expiredDocumentsIsLoading,
      isFetching: expiredDocumentsIsFetching,
    },
  ] = useLazyGetExpiredDocumentsQuery();
  const [getAllowedCategories] =
    useLazyGetCustomerOrganizationAllowedDocumentsCategoriesQuery();
  const [
    getCustomerDocumentFile,
    { isLoading: downloadInProgress, isFetching },
  ] = useLazyGetCustomerDocumentFileQuery();
  const [deleteCustomerDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteCustomerDocumentsMutation();
  const [
    changeCustomerDocumentsStatus,
    { isLoading: statusChangeInProgress, isSuccess: statusChangeIsSuccess },
  ] = useChangeCustomerDocumentsStatusMutation();
  const [changeCustomerDocumentsCategory, { isLoading: moveInProgress }] =
    useChangeCustomerDocumentsCategoryMutation();
  const [
    getAgreements,
    { isLoading: agreementsIsLoading, isSuccess: agreementsLoadingIsSuccess },
  ] = useLazyGetCustomerAgreementsQuery();
  const [
    getUnsignedAgreements,
    {
      isLoading: usignedAgreementsIsLoading,
      isSuccess: unsignedAgreementsLoadingIsSuccess,
    },
  ] = useLazyGetCustomerUnsignedAgreementsQuery();
  const [getCustomerOrganizationDocumentsStats] =
    useLazyGetCustomerOrganizationDocumentsStatsQuery();
  const [rename, { isLoading: documentRenameInProgress }] =
    useRenameCustomerDocumentMutation();
  const [getStats] = useLazyGetCustomerOrganizationDocumentsStatsQuery();

  const [
    deleteFile,
    { isSuccess: deleteSuccess, isLoading: deleteExpiredDocumentInProgress },
  ] = useDeleteCustomerDocumentMutation();

  const prevSearchText = usePrevious(searchText);
  const prevStatuses = usePrevious(selectedStatuses);

  const dispatch = useAppDispatch();

  const {
    filters,
    sort: expiredSort,
    onSortChange,
    onFilterChange,
  } = useQueryState(schema);

  const { getListAction, setPage, errorList } = useGetList<CustomerDocument>({
    getList: getExpiredDocuments,
    onFilterChange,
    onSortChange,
    itemId: customerId,
    schema,
  });

  let link;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS_ADD, {
      customerId: customerId as string,
    });
  } else {
    link = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS_ADD, {
      customerId: customerId as string,
    });
  }

  const onDeleteExpitedDocument = async () => {
    const response = await deleteFile({
      id: customerId as string,
      documentId: selectedDeleteDocument?.id as string,
    });

    if (response) {
      setDeletePopupVisible(false);

      getStats(customerId as string);
    }
  };

  const onChangeSort = (value: string, propValue: string) => {
    setSort(value);
    setSortProp(propValue);

    let categoryId = '';

    const query = `?take=20&sort=${value}&sortProp=${sortProp}&documentCategoryId=${selectedCategory?.id}${
      selectedStatuses?.length
        ? `&status=${selectedStatuses?.join('&status=')}`
        : ''
    }${searchText ? `&name=${searchText}` : ''}`;

    if (selectedCategory?.id !== 'all') {
      categoryId = selectedCategory?.id as string;
    }

    dispatch(clearAllCursors());

    const body: {
      id: string;
      take: string;
      sort: string;
      sortProp: string;
      reload?: boolean;
      categoryId?: string;
      status?: string[];
      name?: string;
    } = {
      id: customerId as string,
      take: '20',
      sort: value,
      sortProp: propValue,
      reload: true,
    };

    if (categoryId) {
      body.categoryId = categoryId;
    }

    if (selectedStatuses?.length) {
      body.status = selectedStatuses;
    }

    if (searchText) {
      body.name = searchText;
    }

    getCustomerDocuments(body);

    navigate(query);
  };

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      const response = (await rename({
        id: customerId as string,
        documentId: selectedDocumentToRename?.id as string,
        body: {
          name: documentName,
        },
      })) as { error?: ResponseError };

      if (response && !response.error) {
        dispatch(
          onDocumentRaname({
            name: documentName,
            type: selectedDocumentToRename?.documentCategoryId as string,
            documentId: selectedDocumentToRename?.id as string,
          }),
        );

        setRenamePopupVisible(false);
        setSelectedDocumentToRename(undefined);
        setDocumentName('');
      } else if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const onScrollButton = (directory: 'left' | 'right') => {
    if (scrollWrapper && scrollWrapper.current) {
      const currentScroll = scrollWrapper.current.scrollLeft;
      const width = scrollWrapper.current.offsetWidth;

      let scrollValue = 0;

      if (directory === 'right') {
        scrollValue = currentScroll + 150;

        if (scrollValue + width >= contentScrollWidth - 100) {
          setWithScrollButton(false);
        }
      } else if (directory === 'left') {
        scrollValue = currentScroll - 150;

        if (contentScrollWidth > currentScroll + width - 150) {
          setWithScrollButton(true);
        }

        if (scrollValue <= 0) {
          setWithLeftButton(false);
        }
      }

      scrollWrapper.current.scroll({
        left: scrollValue,
        behavior: 'smooth',
      });

      setCurrentValueScroll(scrollValue);
    }
  };

  const onGroupByIdSelect = ({
    id,
    selected,
  }: {
    id: string;
    selected: boolean;
  }) => {
    dispatch(onSelectAllGroupById({ id, selected }));
  };

  const onGroupSelect = ({
    type,
    selected,
  }: {
    type: string;
    selected: boolean;
  }) => {
    dispatch(onSelectAllGroup({ type, selected }));
  };

  const changeCustomerDocumentsStatusAction = (
    status: CustomerDocumentStatus,
    selectedIds: string[],
  ) => {
    if (selectedIds.length) {
      changeCustomerDocumentsStatus({
        id: customerId as string,
        body: {
          ids: selectedIds,
          status,
        },
      });
    }
  };

  const onDelete = async (selectedIds: string[]) => {
    if (selectedIds.length) {
      const response = await deleteCustomerDocuments({
        id: customerId as string,
        documentId: selectedIds,
      });

      if (response) {
        setDeletePopupVisible(false);

        dispatch(deleteIsSuccess({ ids: selectedIds }));
        getCustomerOrganizationDocumentsStats(customerId as string);

        getListAction({
          filters: {
            ...filters,
            take: {
              value: ['20'],
              type: SchemaFilterType.STRING,
            },
            status: {
              value: ['EXPIRED'],
              type: SchemaFilterType.STRING,
            },
          },
          sort: expiredSort,
        });
      }
    }
  };

  const onSelectStatuses = async (statuses: string[]) => {
    setSelectedStatuses(statuses);

    let categoryId = '';

    let query = `?take=20&documentCategoryId=${selectedCategory?.id}&sort=${sort}&sortProp=${sortProp}${
      searchText ? `&name=${searchText}` : ''
    }`;

    if (selectedCategory?.id !== 'all') {
      categoryId = selectedCategory?.id as string;
    }

    if (statuses?.length) {
      query = `${query}&status=${statuses.join('&status=')}`;
    }

    navigate(query);

    const body: {
      id: string;
      take: string;
      sort: string;
      sortProp: string;
      categoryId?: string;
      status?: string[];
      name?: string;
      reload: boolean;
    } = {
      id: customerId as string,
      sort,
      sortProp,
      take: '20',
      reload: true,
    };

    if (categoryId) {
      body.categoryId = categoryId;
    }

    if (searchText) {
      body.name = searchText;
    }

    if (statuses?.length) {
      body.status = statuses;
    }

    dispatch(clearAllCursors());

    const response = (await getCustomerDocuments(body)) as {
      error?: any;
      data: any;
    };

    if (response && !response.error) {
      if (categoryId !== 'all') {
        dispatch(
          setCursorByCategoryId({
            startCursor: response?.data?.data?.pageInfo.startCursor,
            endCursor: response?.data?.data?.pageInfo.endCursor,
            take: '20',
            categoryId,
          }),
        );
      }

      if (!categoryId) {
        dispatch(
          setCursor({
            startCursor: response?.data?.data?.pageInfo.startCursor,
            endCursor: response?.data?.data?.pageInfo.endCursor,
            take: '20',
          }),
        );
      }
    }
  };

  const getFiles = async (selectedDocuments: CustomerDocument[]) => {
    if (selectedDocuments.length) {
      const downloadedFiles = await Promise.all(
        selectedDocuments.map(async (item) => {
          const response = await getCustomerDocumentFile({
            id: customerId as string,
            documentId: item.id,
          });
          return response.data;
        }),
      );
      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;
          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = selectedDocuments[index].name;
          hiddenElement.click();
        });
        dispatch(clearSelectedState());
      }
    }
  };

  const onSetPage = async (direction: 'prev' | 'next') => {
    let documentsObject;

    if (selectedCategory?.id === 'all') {
      documentsObject = allDocuments;
    } else if (selectedCategory?.id !== 'all') {
      documentsObject = documentsByCategoryId;
    }

    let cursorValue = '';

    if (selectedCategory) {
      if (selectedCategory?.id !== 'all') {
        if (cursorByCategoryId && cursorByCategoryId[selectedCategory.id]) {
          if (direction === 'next') {
            cursorValue = cursorByCategoryId[selectedCategory.id]
              .endCursor as string;
          } else if (direction === 'prev') {
            cursorValue = cursorByCategoryId[selectedCategory.id]
              .startCursor as string;
          }
        }
      } else if (selectedCategory.id === 'all') {
        if (cursor) {
          if (direction === 'next') {
            cursorValue = cursor.endCursor;
          } else if (direction === 'prev') {
            cursorValue = cursor.startCursor;
          }
        }
      }

      let query = search
        .split('&')
        ?.filter((item) => !item.includes('cursor') && !item.includes('take'))
        ?.join('&');

      let requestAvailable = true;

      if (documentsObject && documentsObject[selectedCategory.id]) {
        let cursorItem;

        if (
          selectedCategory.id !== 'all' &&
          cursorByCategoryId &&
          cursorByCategoryId[selectedCategory.id]
        ) {
          cursorItem = cursorByCategoryId[selectedCategory.id];
        } else if (selectedCategory.id === 'all' && cursor) {
          cursorItem = cursor;
        }

        if (
          cursorItem &&
          direction === 'prev' &&
          documentsObject[selectedCategory.id]?.find(
            (item) => item.prevStartCursor === cursorItem.startCursor,
          )
        ) {
          requestAvailable = false;

          const prevPage = documentsObject[selectedCategory.id]?.find(
            (item) => item.prevStartCursor === cursorItem.startCursor,
          );

          if (prevPage) {
            if (prevPage.hasPrev) {
              query = `${query || ''}${
                query ? '&' : '?'
              }cursor=${cursorValue}&take=-20`;
            }

            if (selectedCategory.id !== 'all') {
              dispatch(
                setCursorByCategoryId({
                  startCursor: prevPage.startCursor,
                  endCursor: prevPage.endCursor,
                  take: '-20',
                  categoryId: selectedCategory.id,
                }),
              );
            } else if (selectedCategory.id === 'all') {
              dispatch(
                setCursor({
                  startCursor: prevPage.startCursor,
                  endCursor: prevPage.endCursor,
                  take: '-20',
                }),
              );
            }
          }
        } else if (
          cursorItem &&
          direction === 'next' &&
          documentsObject[selectedCategory.id]?.find(
            (item) => item.prevEndCursor === cursorItem.endCursor,
          )
        ) {
          requestAvailable = false;

          query = `${query || ''}${
            query ? '&' : '?'
          }cursor=${cursorValue}&take=20`;

          const nextPage = documentsObject[selectedCategory.id]?.find(
            (item) => item.prevEndCursor === cursorItem.endCursor,
          );

          if (nextPage) {
            if (selectedCategory.id !== 'all') {
              dispatch(
                setCursorByCategoryId({
                  startCursor: nextPage.startCursor,
                  endCursor: nextPage.endCursor,
                  take: '20',
                  categoryId: selectedCategory.id,
                }),
              );
            } else if (selectedCategory.id === 'all') {
              dispatch(
                setCursor({
                  startCursor: nextPage.startCursor,
                  endCursor: nextPage.endCursor,
                  take: '20',
                }),
              );
            }
          }
        }
      }

      if (requestAvailable) {
        const body: {
          id: string;
          cursor: string;
          take: string;
          sort: string;
          sortProp: string;
          categoryId?: string;
          name?: string;
          status?: string[];
        } = {
          id: customerId as string,
          cursor: cursorValue,
          take: direction === 'next' ? '20' : '-20',
          sort,
          sortProp,
        };

        if (selectedCategory.id !== 'all') {
          body.categoryId = selectedCategory.id;
        }

        if (searchText) {
          body.name = searchText;
        }

        if (selectedStatuses?.length) {
          body.status = selectedStatuses;
        }

        query = `${query || ''}${query ? '&' : '?'}cursor=${cursorValue}&take=${
          direction === 'next' ? '20' : '-20'
        }${searchText ? `&name=${searchText}` : ''}`;

        const response = await getCustomerDocuments(body);

        if (response?.data?.data) {
          if (selectedCategory.id !== 'all') {
            dispatch(
              setCursorByCategoryId({
                startCursor: response?.data?.data.pageInfo.startCursor,
                endCursor: response?.data?.data.pageInfo.endCursor,
                take: direction === 'next' ? '20' : '-20',
                categoryId: selectedCategory.id,
              }),
            );
          } else if (selectedCategory.id === 'all') {
            dispatch(
              setCursor({
                startCursor: response?.data?.data.pageInfo.startCursor,
                endCursor: response?.data?.data.pageInfo.endCursor,
                take: direction === 'next' ? '20' : '-20',
              }),
            );
          }
        }
      }

      navigate(`${query.includes('?') ? '' : '?'}${query}`);
    }
  };

  const onSearch = (
    text: string,
    withoutTimeout?: boolean,
    category?: string,
  ) => {
    clearTimeout(timeout);

    setSearchText(text);

    let query = `?take=20${
      selectedStatuses?.length
        ? `&status=${selectedStatuses.join('&status=')}`
        : ''
    }&documentCategoryId=${
      category || selectedCategory?.id
    }&sort=${sort}&sortProp=${sortProp}`;

    let categoryId = '';

    if (text) {
      query = `${query}&name=${text}`;
    }

    if ((category && category !== 'all') || selectedCategory?.id !== 'all') {
      categoryId = category || (selectedCategory?.id as string);
    }

    timeout = setTimeout(
      async () => {
        navigate(query);

        const response = (await getCustomerDocuments({
          id: customerId as string,
          categoryId: categoryId === 'all' ? '' : categoryId,
          take: '20',
          name: text,
          reload: true,
          sort,
          sortProp,
        })) as { error?: any; data: any };

        if (response && !response.error) {
          if (categoryId !== 'all') {
            dispatch(
              setCursorByCategoryId({
                startCursor: response?.data?.data?.pageInfo.startCursor,
                endCursor: response?.data?.data?.pageInfo.endCursor,
                take: '20',
                categoryId,
              }),
            );
          }

          if (categoryId === 'all' || !categoryId) {
            dispatch(
              setCursor({
                startCursor: response?.data?.data?.pageInfo.startCursor,
                endCursor: response?.data?.data?.pageInfo.endCursor,
                take: '20',
              }),
            );
          }
        }
      },
      withoutTimeout ? 0 : 700,
    );
  };

  const onScrollToCategory = (categoryId: string) => {
    const currentCategory = document.getElementById(categoryId);

    if (
      scrollWrapper &&
      scrollWrapper.current &&
      currentCategory &&
      scrollWrapper?.current?.offsetWidth < scrollWrapper?.current?.scrollWidth
    ) {
      let left = currentCategory.offsetLeft;

      if (scrollWrapper && scrollWrapper.current) {
        const width = scrollWrapper.current.offsetWidth;

        setCurrentValueScroll(left);

        if (left + width >= contentScrollWidth - 100) {
          setWithScrollButton(false);
        }

        if (
          prevScrollValue &&
          withLeftButton &&
          (left < prevScrollValue || left > prevScrollValue)
        ) {
          left -= 30;
        }

        scrollWrapper.current.scroll({
          left,
          behavior: 'smooth',
        });
      }
    }
  };

  const onCategoryClick = (
    value: { id: string; category: string },
    reload?: boolean,
  ) => {
    setSelectedCategory(value);

    let query = `?documentCategoryId=${value.id}${
      selectedStatuses?.length
        ? `&status=${selectedStatuses?.join('&status=')}`
        : ''
    }&sort=${sort}&sortProp=${sortProp}`;

    let categoryId = '';

    if (value.id !== 'all') {
      categoryId = value.id;
    }

    let requestAvailable = true;

    if (!searchText) {
      if (
        value.id === 'all' &&
        needToReloadCategoryId !== 'all' &&
        allDocuments
      ) {
        if (cursor && cursor?.startCursor && cursor?.endCursor && !reload) {
          requestAvailable = false;

          const currentPage = allDocuments?.all.find(
            (item) => item.endCursor === cursor.endCursor,
          );

          if (currentPage) {
            query = `${query}${
              currentPage.prevEndCursor || currentPage.prevStartCursor
                ? `&cursor=${
                    currentPage.prevEndCursor
                      ? currentPage.prevEndCursor
                      : currentPage.prevStartCursor
                  }`
                : ''
            }&take=${cursor?.take || '20'}`;
          }
        }
      } else if (
        value.id !== 'all' &&
        cursorByCategoryId &&
        cursorByCategoryId[value.id] &&
        cursorByCategoryId[value.id]?.startCursor &&
        cursorByCategoryId[value.id]?.endCursor &&
        !reload &&
        needToReloadCategoryId !== value.id
      ) {
        requestAvailable = false;

        if (documentsByCategoryId && documentsByCategoryId[value.id]) {
          const currentPage = documentsByCategoryId[value.id].find(
            (item) => item.endCursor === cursorByCategoryId[value.id].endCursor,
          );

          if (currentPage) {
            query = `${query}${
              currentPage.prevEndCursor || currentPage.prevStartCursor
                ? `&cursor=${
                    currentPage.prevEndCursor
                      ? currentPage.prevEndCursor
                      : currentPage.prevStartCursor
                  }`
                : ''
            }&take=${cursor?.take || '20'}`;
          }
        }
      }

      navigate(query);

      if (requestAvailable) {
        getCustomerDocuments({
          id: customerId as string,
          categoryId,
          take: '20',
          reload: Boolean(needToReloadCategoryId === value.id) || reload,
          status: selectedStatuses,
          sort,
          sortProp,
        });
      }
    } else {
      onSearch(searchText, true, value.id);
    }

    onScrollToCategory(value.id);
  };

  const getFile = async (documentItem: CustomerDocument) => {
    const response = await getCustomerDocumentFile({
      id: customerId as string,
      documentId: documentItem.id,
    });

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentItem.name;
      hiddenElement.click();

      setSelectedSubMenuDocumentId('');
      setSubMenuVisible(false);
    }
  };

  const changeCustomerDocumentsCategoryAction = async (
    documentCategoryId: string,
    selectedIds: string[],
  ) => {
    if (selectedIds.length) {
      const body: {
        ids: string[];
        documentCategoryId?: string | undefined;
      } = {
        ids: selectedIds,
        documentCategoryId,
      };

      const response = (await changeCustomerDocumentsCategory({
        id: customerId as string,
        body,
      })) as { error?: any };

      if (response && !response.error) {
        getCustomerOrganizationDocumentsStats(customerId as string);

        dispatch(
          onDocumentsMoveById({
            documentCategoryId,
            ids: selectedIds,
            type: selectedCategory?.id,
          }),
        );

        if (documentCategoryId === selectedCategory?.id) {
          const currentCategory = allowedCategories?.find(
            (item) => item.id === documentCategoryId,
          );

          clearCursorByCategoryId(documentCategoryId);

          if (currentCategory) {
            onCategoryClick(
              currentCategory as { id: string; category: string },
              true,
            );
          }
        } else {
          dispatch(setNeedToReloadCategoryId(selectedCategory?.id as string));
        }
      }
    }
  };

  useEffect(() => {
    if (statusChangeIsSuccess) {
      dispatch(clearSelectedState());
      setSelectedStatus(undefined);
      setOtherActionsMenuVisible(false);
    }
  }, [statusChangeIsSuccess]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (scrollWrapper && scrollWrapper.current) {
        if (
          scrollWrapper?.current?.scrollWidth >
          scrollWrapper?.current?.offsetWidth
        ) {
          setWithScrollButton(true);
        } else {
          setWithScrollButton(false);
        }

        setContentScrollWidth(scrollWrapper?.current?.scrollWidth);
      }
    });

    if (wrapperRef && wrapperRef.current && allowedCategories?.length) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [wrapperRef, scrollWrapper, allowedCategories]);

  useEffect(() => {
    if (currentScrollValue > 0) {
      setWithLeftButton(true);
    }
  }, [currentScrollValue]);

  useEffect(() => {
    if (search && allowedCategories) {
      const parsed = queryString.parse(search);

      if (parsed) {
        if (parsed.documentCategoryId && !selectedCategory) {
          if (parsed.documentCategoryId === 'all') {
            setSelectedCategory({ id: 'all', category: 'all' });
          } else if (parsed.documentCategoryId !== 'all') {
            const currentCategory = allowedCategories?.find(
              (item) => item.id === parsed.documentCategoryId,
            );

            onScrollToCategory(parsed.documentCategoryId as string);

            if (currentCategory) {
              setSelectedCategory({
                id: currentCategory.id,
                category: currentCategory?.category || '',
              });
            }
          }
        }
      }
    } else if (!search && !selectedCategory) {
      setSelectedCategory({ id: 'all', category: 'all' });
      navigate(`?documentCategoryId=all&sort=${sort}&sortProp=${sortProp}`);
    }
  }, [search, allowedCategories]);

  useEffect(() => {
    if (selectedCategory && search) {
      const parsed = queryString.parse(search);

      if (
        selectedCategory.id === 'all' &&
        allDocuments &&
        !cursor &&
        allDocuments.all
      ) {
        const currentValues = allDocuments.all[0];

        if (currentValues) {
          dispatch(
            setCursor({
              startCursor: currentValues.startCursor,
              endCursor: currentValues.endCursor,
              take: (parsed?.take as string) || '20',
            }),
          );
        }
      } else if (
        selectedCategory.id !== 'all' &&
        documentsByCategoryId &&
        documentsByCategoryId[selectedCategory.id] &&
        !cursorByCategoryId
      ) {
        const currentValues = documentsByCategoryId[selectedCategory.id][0];

        if (currentValues) {
          dispatch(
            setCursorByCategoryId({
              startCursor: currentValues.startCursor,
              endCursor: currentValues.endCursor,
              take: (parsed?.take as string) || '20',
              categoryId: selectedCategory.id,
            }),
          );
        }
      }
    }
  }, [
    cursor,
    cursorByCategoryId,
    search,
    allDocuments,
    documentsByCategoryId,
    selectedCategory,
  ]);

  useEffect(() => {
    if (deleteSuccess) {
      setDeleteExpiredDocumentPopupVisible(false);
      setSelectedDeleteDocument(undefined);

      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['20'],
            type: SchemaFilterType.STRING,
          },
          status: {
            value: ['EXPIRED'],
            type: SchemaFilterType.STRING,
          },
        },
        sort: expiredSort,
      });
    }
  }, [deleteSuccess]);

  const formattedExpiredDocumentsData = useMemo(() => {
    const nextExpiredDocuments = expiredDocuments?.map((item) => {
      const result = { ...item };

      let documentLink;

      if (customerType === CustomerType.INDIVIDUAL) {
        documentLink = generatePath(
          ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS_INFO,
          {
            customerId: customerId as string,
            documentId: item.id,
          },
        );
      } else {
        documentLink = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS_INFO, {
          customerId: customerId as string,
          documentId: item.id,
        });
      }

      result.url = documentLink;

      return result;
    });

    const documentRenameAvailable =
      customerType === CustomerType.INDIVIDUAL
        ? activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
          )
        : activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
          );

    const data = formatExpiredDocumentsList(
      nextExpiredDocuments as CustomerDocument[],
      setSubMenuVisible,
      setSelectedSubMenuDocumentId,
      setRenamePopupVisible,
      setSelectedDocumentToRename,
      getFile,
      setDeleteExpiredDocumentPopupVisible,
      setSelectedDeleteDocument,
      customerType,
      subMenuVisible,
      selectedSubMenuDocumentId,
      documentRenameAvailable,
      activePermissions,
      search,
    );

    return data;
  }, [
    expiredDocuments,
    subMenuVisible,
    setSubMenuVisible,
    selectedSubMenuDocumentId,
    setSelectedSubMenuDocumentId,
    search,
    customerType,
    activePermissions,
  ]);

  let agreementsContent;

  if (agreementsIsLoading || usignedAgreementsIsLoading) {
    agreementsContent = (
      <div className={cn.agreementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    agreementsContent = (
      <>
        <button
          type="button"
          className={cn.agreementsButton}
          onClick={() => setAgreementsVisible(!agreementsVisible)}
        >
          <div className={cn.agreementsButtonLeft}>
            <span>
              <IconAgreements />
            </span>
            <span>Agreements</span>
          </div>
          <div className={cn.agreementsButtonIcon}>
            {agreementsVisible ? <IconUp /> : <IconDown />}
          </div>
        </button>
        {agreementsVisible && (
          <div className={cn.agreementsContent}>
            <AgreementsHeader />
            {Object.keys(agreementsList).map((item) => (
              <AgreementContent
                key={item}
                agreementGroup={agreementsList[item]}
                customerType={customerType}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  useEffect(() => {
    if (agreementsLoadingIsSuccess && unsignedAgreementsLoadingIsSuccess) {
      const nextAgreements: CustomerAgreement[] = [];

      if (agreements?.length) {
        agreements.forEach((item) => {
          nextAgreements.push(item);
        });
      }

      if (usignedAgreements?.length) {
        usignedAgreements.forEach((item) => {
          nextAgreements.push(item);
        });
      }

      const groupedAgreements = groupBy(
        nextAgreements.sort((a, b) => b.version - a.version),
        (match) => match.type,
      );

      setAgreementsList(groupedAgreements);
    }
  }, [
    agreements,
    usignedAgreements,
    agreementsLoadingIsSuccess,
    unsignedAgreementsLoadingIsSuccess,
  ]);

  useEffect(() => {
    if (customerId && !selectedCategory) {
      if (
        (customerType === CustomerType.INDIVIDUAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
          )) ||
        (customerType === CustomerType.CORPORATE &&
          activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
          ))
      ) {
        const body: {
          id: string;
          take: string;
          sort: string;
          categoryId?: string;
          cursor?: string;
          name?: string;
          status?: string[];
          sortProp: string;
        } = {
          id: customerId,
          take: '20',
          sort,
          sortProp,
        };

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.documentCategoryId !== 'all') {
            body.categoryId = parsed.documentCategoryId as string;
          }

          if (parsed.cursor) {
            body.cursor = parsed.cursor as string;
          }

          if (parsed.take) {
            body.take = parsed.take as string;
          }

          if (parsed.name) {
            body.name = parsed.name as string;

            setSearchText(parsed.name as string);
          }

          if (parsed.status) {
            setSelectedStatuses(
              Array.isArray(parsed.status)
                ? (parsed.status as string[])
                : [parsed.status],
            );
            body.status = Array.isArray(parsed.status)
              ? (parsed.status as string[])
              : [parsed.status];
          }

          if (parsed.sort) {
            setSort(parsed.sort as string);

            body.sort = parsed.sort as string;
          }

          if (parsed.sortProp) {
            setSortProp(body.sortProp);
          }
        }

        getCustomerDocuments(body);
        getAllowedCategories();
        getAgreements(customerId);
        getUnsignedAgreements(customerId);
        getCustomerOrganizationDocumentsStats(customerId);
      }
    }
  }, [customerId, activePermissions, customerType, search, selectedCategory]);

  useEffect(() => {
    if (customerId) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['20'],
            type: SchemaFilterType.STRING,
          },
          status: {
            value: ['EXPIRED'],
            type: SchemaFilterType.STRING,
          },
        },
        sort: expiredSort,
      });
    }
  }, [customerId]);

  useEffect(() => {
    if (
      localStorage.getItem('blockView') &&
      localStorage.getItem('blockView') === 'true'
    ) {
      dispatch(setBlockView(true));
    }
  }, [dispatch, localStorage]);

  useEffect(() => {
    if (
      selectedCategory &&
      documentsByCategoryId &&
      documentsByCategoryId[selectedCategory.id] &&
      ((cursorByCategoryId && !cursorByCategoryId[selectedCategory.id]) ||
        !cursorByCategoryId)
    ) {
      dispatch(
        setCursorByCategoryId({
          startCursor:
            documentsByCategoryId[selectedCategory.id][0].startCursor,
          endCursor: documentsByCategoryId[selectedCategory.id][0].endCursor,
          take: '20',
          categoryId: selectedCategory.id,
        }),
      );
    }
  }, [documentsByCategoryId, cursorByCategoryId, selectedCategory]);

  useEffect(() => {
    if (selectedDocumentToRename) {
      setDocumentName(selectedDocumentToRename.name);
    }
  }, [selectedDocumentToRename]);

  useEffect(() => {
    if (selectedDocumentsByGroupId) {
      let counter = 0;

      Object.keys(selectedDocumentsByGroupId)?.forEach((key) => {
        counter += selectedDocumentsByGroupId[key];
      });

      setSelectedDocumentsCountByCategory(counter);
    }
  }, [selectedDocumentsByGroupId]);

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (customerType === CustomerType.INDIVIDUAL) {
      if (
        individualBaseInformation?.firstName ||
        individualBaseInformation?.lastName
      ) {
        title = `${individualBaseInformation?.firstName} ${individualBaseInformation?.lastName}: Documents`;
      } else {
        title = 'Documents';
      }
    } else if (corporateBaseInformation?.name) {
      title = `${corporateBaseInformation?.name}: Documents`;
    } else {
      title = 'Documents';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [
    individualBaseInformation?.firstName,
    individualBaseInformation?.lastName,
    corporateBaseInformation?.name,
  ]);

  let content;

  if (
    (customerType === CustomerType.INDIVIDUAL &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
      )) ||
    (customerType === CustomerType.CORPORATE &&
      activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ))
  ) {
    const currentCategoryCursor =
      selectedCategory && selectedCategory?.id === 'all'
        ? cursor
        : cursorByCategoryId &&
          selectedCategory &&
          cursorByCategoryId[selectedCategory.id];

    const currentCategoryDocuments =
      selectedCategory && selectedCategory?.id === 'all'
        ? allDocuments?.all
        : documentsByCategoryId &&
          selectedCategory &&
          documentsByCategoryId[selectedCategory.id];

    const currentCategoryDocumentsValues = currentCategoryCursor
      ? currentCategoryDocuments?.find(
          (item) =>
            item.startCursor === currentCategoryCursor?.startCursor &&
            item.endCursor === currentCategoryCursor?.endCursor,
        )
      : undefined;

    const organizationStatus =
      customerType === CustomerType.INDIVIDUAL
        ? individualBaseInformation?.status
        : corporateBaseInformation?.status;

    content = (
      <>
        {!expiredDocumentsVisible && (
          <>
            {expiredDocuments &&
              expiredDocuments?.length > 0 &&
              expiredDocumentsPagination && (
                <div className={cn.customerDocumentsExpiredBlock}>
                  <div className={cn.customerDocumentsExpiredBlockContent}>
                    <IconAttention />
                    <div
                      className={cn.customerDocumentsExpiredBlockContentText}
                    >
                      {`${expiredDocumentsPagination?.totalCount} document${
                        expiredDocumentsPagination?.totalCount > 1 ? 's' : ''
                      } expired`}
                    </div>
                    <button
                      className={cn.customerDocumentsExpiredBlockLink}
                      type="button"
                      onClick={() => setExpiredDocumentsVisible(true)}
                    >
                      Show
                    </button>
                  </div>
                </div>
              )}
            <div className={cn.customerDocumentsTop}>
              <div className={cn.customerDocumentsTopLeft}>
                {((customerType === CustomerType.INDIVIDUAL &&
                  activePermissions?.includes(
                    PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_CREATE_ACTION,
                  )) ||
                  (customerType === CustomerType.CORPORATE &&
                    activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_CREATE_ACTION,
                    ))) &&
                  organizationStatus !== CustomerStatus.CLOSED && (
                    <NavLink
                      to={`${link}${search || ''}`}
                      className={cn.customersDocumentsCreateLink}
                    >
                      <div className={cn.customersDocumentsCreateLinkContent}>
                        <IconExpand
                          className={cn.customersDocumentsCreateLinkIcon}
                        />
                        <span>Add documents</span>
                      </div>
                    </NavLink>
                  )}
                {(allDocumentsSelected > 0 ||
                  selectedDocumentsCountByCategory > 0) && (
                  <div className={cn.selectedDocumentsControls}>
                    <ClickOutsideDetector
                      className={cn.selectedDocumentsMoveWrapper}
                      setOptionsListVisible={setMenuVisible}
                    >
                      <>
                        <button
                          type="button"
                          className={`${cn.selectedDocumentsButton} ${
                            menuVisible ? cn.selectedDocumentsButtonActive : ''
                          }`}
                          onClick={() => setMenuVisible(!menuVisible)}
                          disabled={moveInProgress}
                        >
                          <IconArrowRight
                            className={cn.selectedDocumentsButtonIcon}
                            width={10}
                            height={10}
                          />
                          {moveInProgress ? 'Loading...' : 'Move to group'}
                        </button>
                        {menuVisible && (
                          <ul className={cn.selectedDocumentsMenu}>
                            <li className={cn.selectedDocumentsMenuTitle}>
                              Which group to move &nbsp;
                              {selectedCategory &&
                              selectedCategory?.id === 'all'
                                ? allDocumentsSelected
                                : selectedDocumentsCountByCategory}
                              &nbsp; documents to?
                            </li>
                            {allowedCategories?.map((item) => (
                              <li key={item.id}>
                                <button
                                  type="button"
                                  className={cn.selectedDocumentsMenuButton}
                                  onClick={() => {
                                    const selectedIds: string[] = [];

                                    allowedCategories?.forEach(
                                      (allowedCategory) => {
                                        if (
                                          documentsByCategoryId &&
                                          documentsByCategoryId[
                                            allowedCategory.id
                                          ]
                                        ) {
                                          documentsByCategoryId[
                                            allowedCategory.id
                                          ]?.forEach((category) => {
                                            category.documents?.forEach(
                                              (document) => {
                                                if (document.isSelected) {
                                                  selectedIds.push(document.id);
                                                }
                                              },
                                            );
                                          });
                                        }
                                      },
                                    );

                                    if (allDocuments) {
                                      allDocuments.all?.forEach((category) => {
                                        category.documents?.forEach(
                                          (document) => {
                                            if (
                                              document.isSelected &&
                                              !selectedIds.find(
                                                (id) => id === document.id,
                                              )
                                            ) {
                                              selectedIds.push(document.id);
                                            }
                                          },
                                        );
                                      });
                                    }

                                    changeCustomerDocumentsCategoryAction(
                                      item.id,
                                      selectedIds,
                                    );

                                    setMenuVisible(false);
                                  }}
                                >
                                  {item.name.includes('Add other document')
                                    ? 'Other documents'
                                    : item.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    </ClickOutsideDetector>
                    <ClickOutsideDetector
                      className={cn.selectedDocumentsOtherActions}
                      setOptionsListVisible={setOtherActionsMenuVisible}
                    >
                      <>
                        <button
                          type="button"
                          className={cn.otherActionsMenuButton}
                          aria-label="Other actions"
                          onClick={() =>
                            setOtherActionsMenuVisible(!otherActionsMenuVisible)
                          }
                        >
                          <span />
                          <span />
                          <span />
                        </button>
                        {otherActionsMenuVisible && (
                          <ul className={cn.otherActionsMenuList}>
                            {((customerType === CustomerType.INDIVIDUAL &&
                              activePermissions?.includes(
                                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                              )) ||
                              (customerType === CustomerType.CORPORATE &&
                                activePermissions?.includes(
                                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                                ))) && (
                              <>
                                <li>
                                  <button
                                    type="button"
                                    className={cn.otherActionMenuButton}
                                    disabled={statusChangeInProgress}
                                    onClick={() => {
                                      setSelectedStatus(
                                        CustomerDocumentStatus.ACCEPTED,
                                      );

                                      const selectedDocuments: string[] = [];

                                      if (
                                        allowedCategories &&
                                        documentsByCategoryId
                                      ) {
                                        allowedCategories?.forEach((item) => {
                                          if (documentsByCategoryId[item.id]) {
                                            documentsByCategoryId[
                                              item.id
                                            ].forEach((category) => {
                                              category.documents?.forEach(
                                                (document) => {
                                                  if (document.isSelected) {
                                                    selectedDocuments.push(
                                                      document.id,
                                                    );
                                                  }
                                                },
                                              );
                                            });
                                          }
                                        });
                                      }

                                      if (allDocuments) {
                                        allDocuments.all.forEach((item) => {
                                          item.documents?.forEach(
                                            (document) => {
                                              if (
                                                document.isSelected &&
                                                !selectedDocuments.find(
                                                  (id) => document.id === id,
                                                )
                                              ) {
                                                selectedDocuments.push(
                                                  document.id,
                                                );
                                              }
                                            },
                                          );
                                        });
                                      }

                                      changeCustomerDocumentsStatusAction(
                                        CustomerDocumentStatus.ACCEPTED,
                                        selectedDocuments,
                                      );
                                    }}
                                  >
                                    {statusChangeInProgress &&
                                    selectedStatus ===
                                      CustomerDocumentStatus.ACCEPTED ? (
                                      <IconLoader
                                        className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                        width={15}
                                        height={15}
                                      />
                                    ) : (
                                      <IconSelected
                                        className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                        width={15}
                                        height={15}
                                      />
                                    )}
                                    {statusChangeInProgress &&
                                    selectedStatus ===
                                      CustomerDocumentStatus.ACCEPTED
                                      ? 'Loading...'
                                      : 'Accept'}
                                  </button>
                                </li>
                                <li>
                                  <button
                                    type="button"
                                    className={cn.otherActionMenuButton}
                                    disabled={statusChangeInProgress}
                                    onClick={() => {
                                      setSelectedStatus(
                                        CustomerDocumentStatus.REJECTED,
                                      );

                                      const selectedDocuments: string[] = [];

                                      if (
                                        allowedCategories &&
                                        documentsByCategoryId
                                      ) {
                                        allowedCategories?.forEach((item) => {
                                          if (documentsByCategoryId[item.id]) {
                                            documentsByCategoryId[
                                              item.id
                                            ].forEach((category) => {
                                              category.documents?.forEach(
                                                (document) => {
                                                  if (document.isSelected) {
                                                    selectedDocuments.push(
                                                      document.id,
                                                    );
                                                  }
                                                },
                                              );
                                            });
                                          }
                                        });
                                      }

                                      if (allDocuments) {
                                        allDocuments.all.forEach((item) => {
                                          item.documents?.forEach(
                                            (document) => {
                                              if (
                                                document.isSelected &&
                                                !selectedDocuments.find(
                                                  (id) => document.id === id,
                                                )
                                              ) {
                                                selectedDocuments.push(
                                                  document.id,
                                                );
                                              }
                                            },
                                          );
                                        });
                                      }

                                      changeCustomerDocumentsStatusAction(
                                        CustomerDocumentStatus.REJECTED,
                                        selectedDocuments,
                                      );
                                    }}
                                  >
                                    {statusChangeInProgress &&
                                    selectedStatus ===
                                      CustomerDocumentStatus.REJECTED ? (
                                      <IconLoader
                                        className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                        width={16}
                                        height={16}
                                      />
                                    ) : (
                                      <IconBlock
                                        className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                        width={16}
                                        height={16}
                                      />
                                    )}
                                    {statusChangeInProgress &&
                                    selectedStatus ===
                                      CustomerDocumentStatus.REJECTED
                                      ? 'Loading...'
                                      : 'Reject'}
                                  </button>
                                </li>
                              </>
                            )}
                            <li>
                              <button
                                type="button"
                                className={cn.otherActionMenuButton}
                                disabled={downloadInProgress || isFetching}
                                onClick={() => {
                                  const selectedDocuments: CustomerDocument[] =
                                    [];

                                  if (
                                    allowedCategories &&
                                    documentsByCategoryId
                                  ) {
                                    allowedCategories.forEach((item) => {
                                      if (documentsByCategoryId[item.id]) {
                                        documentsByCategoryId[item.id].forEach(
                                          (category) => {
                                            category.documents?.forEach(
                                              (document) => {
                                                if (document.isSelected) {
                                                  selectedDocuments.push(
                                                    document,
                                                  );
                                                }
                                              },
                                            );
                                          },
                                        );
                                      }
                                    });
                                  }

                                  if (allDocuments) {
                                    allDocuments.all.forEach((category) => {
                                      category.documents?.forEach(
                                        (document) => {
                                          if (
                                            document.isSelected &&
                                            !selectedDocuments.find(
                                              (selected) =>
                                                selected.id === document.id,
                                            )
                                          ) {
                                            selectedDocuments.push(document);
                                          }
                                        },
                                      );
                                    });
                                  }

                                  getFiles(selectedDocuments);
                                }}
                              >
                                {downloadInProgress || isFetching ? (
                                  <IconLoader
                                    className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                    width={16}
                                    height={16}
                                  />
                                ) : (
                                  <IconDownload
                                    className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                    width={16}
                                    height={16}
                                  />
                                )}
                                Download
                              </button>
                            </li>
                            {((customerType === CustomerType.INDIVIDUAL &&
                              activePermissions?.includes(
                                PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                              )) ||
                              (customerType === CustomerType.CORPORATE &&
                                activePermissions?.includes(
                                  PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                                ))) && (
                              <li>
                                <button
                                  type="button"
                                  className={cn.otherActionMenuButton}
                                  onClick={() => setDeletePopupVisible(true)}
                                >
                                  <IconTrash
                                    className={`${cn.selectedDocumentsButtonIcon} ${cn.selectedDocumentsButtonIconMargin}`}
                                    width={16}
                                    height={16}
                                  />
                                  Delete
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </>
                    </ClickOutsideDetector>
                  </div>
                )}
              </div>
              <div className={cn.customerDocumentsTopRight}>
                <InputText
                  name="search"
                  placeholder="Search by docs..."
                  className={`borderless ${cn.customerDocumentsSearchInput}`}
                  prefix={<IconSearch />}
                  labelClassName={`${cn.customerDocumentsSearchLabel} ${
                    searchText || activeLabel
                      ? cn.customerDocumentsSearchInputActive
                      : ''
                  }`}
                  value={searchText}
                  onChange={(e) => onSearch(e.target.value)}
                  onBlur={() => setActiveLabel(false)}
                  onFocus={() => setActiveLabel(true)}
                />
                {/* <button
            type="button"
            className={`${cn.blockViewButton} ${
              blockView ? cn.rowViewButton : ''
            }`}
            onClick={() => {
              dispatch(setBlockView(!blockView));
              localStorage.setItem('blockView', (!blockView).toString());
            }}
            aria-label="Block view button"
          >
            <span />
            <span />
          </button> */}
              </div>
            </div>
            <div className={cn.agreementsWrapper}>{agreementsContent}</div>
            <div className={cn.documentsCategoriesContent}>
              {withLeftButton && (
                <button
                  type="button"
                  aria-label="Scroll left"
                  className={`${cn.scrollRight} ${cn.scrollLeft}`}
                  onClick={() => onScrollButton('left')}
                >
                  <IconLeft />
                </button>
              )}
              <div
                className={cn.documentsCategoriesWrapper}
                ref={scrollWrapper}
              >
                <div className={cn.documentsCategories}>
                  <button
                    type="button"
                    className={`${cn.documentCategory} ${
                      selectedCategory?.id === 'all' ? cn.active : ''
                    }`}
                    id="all"
                    onClick={() =>
                      onCategoryClick(
                        { id: 'all', category: 'all' },
                        !!(prevSearchText && !searchText) ||
                          (prevStatuses?.length && !selectedStatuses?.length),
                      )
                    }
                  >
                    All documents
                  </button>
                  {allowedCategories?.map((item) => (
                    <button
                      type="button"
                      className={`${cn.documentCategory} ${
                        selectedCategory?.id === item.id ? cn.active : ''
                      } ${
                        stats &&
                        !stats.find(
                          (stat) => stat.documentCategoryId === item.id,
                        )
                          ? cn.withoutDocs
                          : ''
                      }`}
                      id={item.id}
                      key={item.id}
                      onClick={() =>
                        onCategoryClick(
                          item as { id: string; category: string },
                          !!(prevSearchText && !searchText) ||
                            (prevStatuses?.length && !selectedStatuses?.length),
                        )
                      }
                    >
                      {`${
                        item.name.includes('Add other document')
                          ? 'Other documents'
                          : item.name
                      } ${
                        stats?.find(
                          (stat) => stat.documentCategoryId === item.id,
                        )
                          ? `(${
                              stats?.find(
                                (stat) => stat.documentCategoryId === item.id,
                              )?.count
                            })`
                          : ''
                      }`}
                    </button>
                  ))}
                </div>
              </div>
              {withScrollButton && (
                <button
                  type="button"
                  aria-label="Scroll right"
                  className={cn.scrollRight}
                  onClick={() => onScrollButton('right')}
                >
                  <IconRight />
                </button>
              )}
            </div>
            <div className={cn.filtersWrapper}>
              {selectedStatuses?.length > 0 &&
                selectedStatuses?.map((item) => (
                  <FilterTag
                    key={item}
                    type="round"
                    title={
                      <>
                        <span className={cn.filterLabel}>{`Status: `}</span>
                        <span>{`${item.charAt(0)}${item
                          .slice(1)
                          .toLowerCase()}`}</span>
                      </>
                    }
                    className={cn.filterTag}
                    onClick={() => {
                      onSelectStatuses(
                        selectedStatuses?.filter((status) => status !== item),
                      );
                    }}
                  />
                ))}
            </div>
            <CorporateDocuments
              onSelect={(type, selected, id) => {
                if (
                  currentCategoryDocumentsValues?.documents?.find(
                    (item) => item.category,
                  )
                ) {
                  dispatch(
                    onSelectDocument({
                      type,
                      selected,
                      id,
                    }),
                  );
                } else {
                  dispatch(
                    onSelectDocumentById({
                      documentCategoryId: type,
                      selected,
                      id,
                    }),
                  );
                }
              }}
              onGroupSelect={onGroupSelect}
              // blockView={blockView}
              customerId={customerId as string}
              customerType={customerType}
              isLoading={isLoading || documentsIsFetching}
              documentsIsFetching={documentsIsFetching}
              onGroupByIdSelect={onGroupByIdSelect}
              selectedCategory={selectedCategory}
              currentCategoryDocuments={
                currentCategoryDocumentsValues?.documents || []
              }
              allDocumentsSelected={allDocumentsSelected}
              selectedDocumentsyategoryId={
                selectedCategory
                  ? selectedDocumentsByGroupId[selectedCategory.id]
                  : 0
              }
              allDocumentsTotal={allDocumentsTotal}
              documentsByCategoryTotal={
                selectedCategory
                  ? documentsByCategoryTotal[selectedCategory?.id]
                  : 0
              }
              setRenamePopupVisible={setRenamePopupVisible}
              setSelectedDocumentToRename={setSelectedDocumentToRename}
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={onSelectStatuses}
              sort={sort}
              onChangeSort={onChangeSort}
              sortProp={sortProp}
              status={organizationStatus}
              updateExpiredDocumentsState={() =>
                getListAction({
                  filters: {
                    ...filters,
                    take: {
                      value: ['20'],
                      type: SchemaFilterType.STRING,
                    },
                    status: {
                      value: ['EXPIRED'],
                      type: SchemaFilterType.STRING,
                    },
                  },
                  sort: expiredSort,
                })
              }
            />
            <div className={cn.documentsPagination}>
              <button
                type="button"
                className={`${cn.paginationButton} ${cn.buttonPrev} ${
                  !currentCategoryDocumentsValues?.hasPrev ||
                  isLoading ||
                  documentsIsFetching
                    ? cn.disabled
                    : ''
                }`}
                onClick={() => onSetPage('prev')}
                disabled={
                  !currentCategoryDocumentsValues?.hasPrev ||
                  isLoading ||
                  documentsIsFetching
                }
              >
                <IconLeft /> Prev
              </button>
              <button
                type="button"
                className={`${cn.paginationButton} ${cn.buttonNext} ${
                  !currentCategoryDocumentsValues?.hasNext ||
                  isLoading ||
                  documentsIsFetching
                    ? cn.disabled
                    : ''
                }`}
                onClick={() => onSetPage('next')}
                disabled={
                  !currentCategoryDocumentsValues?.hasNext ||
                  isLoading ||
                  documentsIsFetching
                }
              >
                Next <IconRight />
              </button>
            </div>
          </>
        )}
        {expiredDocumentsVisible && (
          <>
            <button
              type="button"
              className={cn.documentsBackButton}
              onClick={() => setExpiredDocumentsVisible(false)}
            >
              <IconArrowLeft width={13} height={11} />
              Back to documents
            </button>
            <div className={cn.expiredDocumentsTitle}>Expired documents</div>
            <div className={cn.expiredDocumentsTableWrapper}>
              <Table
                take="20"
                setPage={(data) => {
                  setPage({
                    ...data,
                    filters: {
                      ...filters,
                      status: {
                        value: ['EXPIRED'],
                        type: SchemaFilterType.STRING,
                      },
                    },
                  });
                }}
                tableFormClassName={cn.expiredDocumentsTable}
                tableTopClassName={cn.expiredDocumentsTableTop}
                pagination={expiredDocumentsPagination}
                columns={columns}
                data={formattedExpiredDocumentsData || []}
                onSort={() => {}}
                filters={filters}
                sort={expiredSort}
                columnsLabels={columnsLabels}
                onFilterChange={() => {}}
                isLoading={
                  expiredDocumentsIsFetching || expiredDocumentsIsLoading
                }
                error={errorList}
                clickable
              />
            </div>
          </>
        )}
        <Outlet />
      </>
    );
  } else if (
    (customerType === CustomerType.INDIVIDUAL &&
      !activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
      )) ||
    (customerType === CustomerType.CORPORATE &&
      !activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ))
  ) {
    content = <PermissionDenied />;
  }

  return (
    <div
      className={`${cn.customerDocumentsWrapper} ${
        (customerType === CustomerType.INDIVIDUAL &&
          !activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
          )) ||
        (customerType === CustomerType.CORPORATE &&
          !activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
          ))
          ? cn.flexBox
          : ''
      }`}
    >
      <Popup
        title={`Delete ${
          selectedCategory && selectedCategory?.id === 'all'
            ? allDocumentsSelected
            : selectedDocumentsCountByCategory
        } documents?`}
        onCancelClick={() => {
          setDeletePopupVisible(false);
        }}
        onOkClick={() => {
          const selectedIds: string[] = [];

          if (documentsByCategoryId && allowedCategories) {
            allowedCategories.forEach((item) => {
              if (documentsByCategoryId[item.id]) {
                documentsByCategoryId[item.id].forEach((category) => {
                  category.documents?.forEach((document) => {
                    if (document.isSelected) {
                      selectedIds.push(document.id);
                    }
                  });
                });
              }
            });
          }

          if (allDocuments) {
            allDocuments.all.forEach((item) => {
              item.documents?.forEach((document) => {
                if (
                  document.isSelected &&
                  !selectedIds.find((id) => id === document.id)
                ) {
                  selectedIds.push(document.id);
                }
              });
            });
          }

          onDelete(selectedIds);
        }}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
      />
      <Popup
        title="Rename"
        onCancelClick={() => {
          setRenamePopupVisible(false);
          setSelectedDocumentToRename(undefined);
          setDocumentName('');
          setNameError('');
        }}
        onOkClick={onRename}
        cancelButtonText="Cancel"
        okButtonText={documentRenameInProgress ? 'Loading...' : 'Save'}
        isVisible={renamePopupVisible}
        isLoading={documentRenameInProgress}
        okButtonDisabled={documentName === selectedDocumentToRename?.name}
      >
        <InputText
          placeholder="Enter document name..."
          name="documentName"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          labelClassName={cn.renameInput}
          error={nameError || (errors?.name?.message as string)}
        />
      </Popup>
      <Popup
        title={`Delete ${selectedDeleteDocument?.name} documents?`}
        onCancelClick={() => {
          setDeleteExpiredDocumentPopupVisible(false);
        }}
        onOkClick={onDeleteExpitedDocument}
        cancelButtonText="Cancel"
        okButtonText={deleteExpiredDocumentInProgress ? 'Loading...' : 'Delete'}
        isVisible={deleteExpiredDocumentPopupVisible}
        isLoading={deleteExpiredDocumentInProgress}
      />
      {content}
      <div id="delete-popup-wrapper" />
    </div>
  );
};

export default CustomerDocuments;
